import React, { useMemo } from 'react'
import { CopyIcon } from '@/atoms/Icons/CopyIcon'
import { EnvelopeIcon } from '@/atoms/Icons/EnvelopeIcon'
import { FacebookIcon } from '@/atoms/Icons/FacebookIcon'
import { LinkIcon } from '@/atoms/Icons/LinkIcon'
import { LinkedInLettersIcon } from '@/atoms/Icons/LinkedInLettersIcon'
import { RedditIcon } from '@/atoms/Icons/RedditIcon'
import { TelegramIcon } from '@/atoms/Icons/TelegramIcon'
import { TwitterXIcon } from '@/atoms/Icons/TwitterXIcon'
import { OneColorIconProps } from '@/atoms/utils'
import { useToast } from '@/molecules/Toast'
import { useTranslate } from '@/utils/translate/translate-client'

export interface SocialShare {
  name: string
  icon: React.FC<OneColorIconProps>
  href?: string
  onClick?: () => void
  brandColor: string
}

interface UseSocialSharingValues {
  links: SocialShare[]
  socials: Record<string, SocialShare>
}

interface UseSocialSharingProps {
  shareUrl: string
  emailContent?: {
    subject: string
    body: string
  }
}

export const useSocialSharing = ({ shareUrl, emailContent }: UseSocialSharingProps): UseSocialSharingValues => {
  const { t } = useTranslate('common')

  const linkCopied = t('linkCopied', 'Link copied.')

  const toast = useToast()

  const socials = useMemo(
    () => ({
      copyLink: {
        name: t('copyLink', 'Copy Link'),
        icon: LinkIcon,
        onClick: () => {
          navigator.clipboard.writeText(shareUrl)
          toast?.showToast(linkCopied, {
            icon: React.createElement(CopyIcon, { size: 20, color: 'gray-200' }),
          })
        },
        brandColor: '#000',
      },
      twitter: {
        name: 'Twitter',
        icon: TwitterXIcon,
        href: `https://twitter.com/share?url=${shareUrl}`,
        brandColor: '#1DA1F2',
      },
      facebook: {
        name: 'Facebook',
        icon: FacebookIcon,
        href: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
        brandColor: '#1877F2',
      },
      reddit: {
        name: 'Reddit',
        icon: RedditIcon,
        href: `http://reddit.com/submit?url=${shareUrl}`,
        brandColor: '#FF4500',
      },
      telegram: {
        name: 'Telegram',
        icon: TelegramIcon,
        href: `https://telegram.me/share/url?url=${shareUrl}`,
        brandColor: '#27A7E7',
      },
      linkedIn: {
        name: 'LinkedIn',
        icon: LinkedInLettersIcon,
        href: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`,
        brandColor: '#0A66C2',
      },
      email: {
        name: t('email', 'Email'),
        icon: EnvelopeIcon,
        href: emailContent
          ? `mailto:?subject=${encodeURIComponent(emailContent.subject)}&body=${encodeURIComponent(emailContent.body)}`
          : `mailto:?subject=Join%20me%20on%20Angel%20Studios&body=Come%20and%20see%20this%20from%20Angel%20Studios:%0D%0D${shareUrl}`,
        brandColor: '#000',
      },
    }),
    [shareUrl, t, toast, linkCopied, emailContent],
  )

  return {
    socials,
    links: Object.values(socials),
  }
}
